import React, { useState } from 'react';
import useSuspenseFetch from '../hooks/useSuspenseFetch';
import ReactECharts from 'echarts-for-react';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import Slider from 'react-slick';
import { Chip, Box } from '@mui/material';
import { CardHeader, Card, CardContent, Typography } from '@mui/material';
import { SlickDots, slickDotsClass } from './SlickDots';
import { chartColours } from '../theme/constants';
import TypedValue from '../TypedValue';
import { lighten } from '@mui/system';
import { setHours, format } from 'date-fns';

const chartHeaders = [
  'Emails Overview',
  'Opens and Clicks Overview',
  'Subject Line Length vs Open Rate',
  'Send Time Analysis'
];

const scaleSymbolBySent = (sent: number, minSent: number, maxSent: number) => {
  const minSize = 15;
  const maxSize = 75;

  const ratio = (Math.log(sent) - Math.log(minSent)) / (Math.log(maxSent) - Math.log(minSent));

  return minSize + ratio * (maxSize - minSize);
};

const hourToDate = (hour: number) => setHours(new Date(2000, 0, 1, 0, 0, 0), hour);

const Charts = () => {
  const [currentChartIndex, setCurrentChartIndex] = useState(0);
  const { data: sendFrequency } = useSuspenseFetch('reports/emails/created-sent');
  const { data: openClickRates } = useSuspenseFetch('reports/emails/open-click-rates');
  const { data: subjectOpenRates } = useSuspenseFetch('reports/emails/subject-open-rates');
  const { data: sendTimes } = useSuspenseFetch('reports/emails/send-times');

  const minSent = Math.min(...subjectOpenRates.map((d) => d.sent));
  const maxSent = Math.max(...subjectOpenRates.map((d) => d.sent));

  class EmailsCreatedSent {
    windowStart: Date;
    windowEnd: Date;
    campaignCount: number;
    emailCount: number;
    totalSent: number;
  }

  class EmailsOpenClickRates {
    windowDays: number;
    averageTotalOpensRate: number;
    averageUniqueOpensRate: number;
    averageTotalClicksRate: number;
    averageUniqueClicksRate: number;
  }

  class SubjectOpenRates {
    subjectLength: number;
    openRate: number;
    sent: number;
    campaignId: number;
  }

  const subjectOpenRatesChartData = subjectOpenRates.map((e: SubjectOpenRates) => [
    e.subjectLength,
    e.openRate,
    e.sent,
    e.campaignId
  ]);

  const times: number[] = [sendTimes.bestSendTimeForOpens, sendTimes.bestSendTimeForClicks, sendTimes.mostFrequentSendTime];

  let minTime = Math.min(...times);
  let maxTime = Math.max(...times);

  if (Math.abs(maxTime - minTime) < 12) {
    const centre = Math.round((maxTime + minTime) / 2);

    minTime = centre - 6;
    maxTime = centre + 6;
  }

  const chartOptions = [
    {
      color: [chartColours[18].value, chartColours[17].value, chartColours[13].value],
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' }
      },
      legend: {
        data: ['Emails Created', 'Campaigns Sent', 'Volume Sent']
      },
      grid: {
        top: 50,
        left: 60,
        right: 60,
        bottom: 50
      },
      xAxis: {
        type: 'category',
        data: ['0-30 days ago', '30-60 days ago', '60-90 days ago']
      },
      yAxis: [
        {
          type: 'value',
          name: 'Count of Emails/Campaigns',
          nameLocation: 'middle',
          nameRotate: 90,
          nameGap: 40,
          axisLabel: {
            formatter: (value: number) => TypedValue({ value, type: 'compactNumber', accuracy: 0 })
          }
        },
        {
          type: 'value',
          name: 'Volume Sent',
          nameLocation: 'middle',
          nameRotate: 90,
          nameGap: 40,
          axisLabel: {
            formatter: (value: number) => TypedValue({ value, type: 'compactNumber', accuracy: 1 })
          },
          splitLine: {
            show: false
          }
        }
      ],
      series: [
        {
          name: 'Emails Created',
          type: 'bar',
          yAxisIndex: 0,
          data: sendFrequency.map((e: EmailsCreatedSent) => e.emailCount)
        },
        {
          name: 'Campaigns Sent',
          type: 'bar',
          yAxisIndex: 0,
          data: sendFrequency.map((e: EmailsCreatedSent) => e.campaignCount)
        },
        {
          name: 'Volume Sent',
          type: 'line',
          yAxisIndex: 1,
          data: sendFrequency.map((e: EmailsCreatedSent) => e.totalSent),
          smooth: true
        }
      ]
    },
    {
      color: [chartColours[9].value, lighten(chartColours[9].value, 0.5), chartColours[5].value, lighten(chartColours[5].value, 0.5)],
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        valueFormatter: (value: number) => value + '%'
      },
      legend: {
        data: ['Total Open Rate', 'Unique Open Rate', 'Total Click Rate', 'Unique Click Rate'],
        top: 0
      },
      grid: {
        top: 50,
        left: 50,
        right: 10,
        bottom: 50
      },
      xAxis: [
        {
          type: 'category',
          data: openClickRates.map((e: EmailsOpenClickRates) => `Last ${e.windowDays} days`)
        }
      ],
      yAxis: [
        {
          type: 'value',
          axisLabel: {
            formatter: (value: number) => `${value}%`
          }
        }
      ],
      series: [
        {
          name: 'Total Open Rate',
          type: 'bar',
          data: openClickRates.map((e: EmailsOpenClickRates) => TypedValue({ value: e.averageTotalOpensRate * 100, type: 'number', accuracy: 2 }))
        },
        {
          name: 'Unique Open Rate',
          type: 'bar',
          data: openClickRates.map((e: EmailsOpenClickRates) => TypedValue({ value: e.averageUniqueOpensRate * 100, type: 'number', accuracy: 2 }))
        },
        {
          name: 'Total Click Rate',
          type: 'bar',
          data: openClickRates.map((e: EmailsOpenClickRates) => TypedValue({ value: e.averageTotalClicksRate * 100, type: 'number', accuracy: 2 }))
        },
        {
          name: 'Unique Click Rate',
          type: 'bar',
          data: openClickRates.map((e: EmailsOpenClickRates) => TypedValue({ value: e.averageUniqueClicksRate * 100, type: 'number', accuracy: 2 }))
        }
      ]
    },
    {
      tooltip: {
        trigger: 'item',
        formatter: (params) => {
          const [subjectLength, openRate, sent] = params.data;

          return `Sent: <b>${sent.toLocaleString()}</b><br/>Subject Length: <b>${subjectLength}</b><br/>Open Rate: <b>${TypedValue({ value: openRate * 100, type: 'number', accuracy: 2 })}%</b>`;
        }
      },
      grid: {
        top: 20,
        left: 60,
        right: 20,
        bottom: 70
      },
      xAxis: {
        name: 'Subject Length',
        type: 'value',
        nameLocation: 'middle',
        nameGap: 25
      },
      yAxis: {
        name: 'Open Rate',
        type: 'value',
        nameLocation: 'middle',
        nameRotate: 90,
        nameGap: 40,
        axisLabel: {
          formatter: (value: number) => `${(value * 100).toFixed(0)}%`
        }
      },
      series: [
        {
          type: 'scatter',
          symbolSize: (value) => scaleSymbolBySent(value[2], minSent, maxSent),
          data: subjectOpenRatesChartData,
          itemStyle: {
            color: chartColours[0].value,
            opacity: 0.2
          },
          silent: true,
          z: 1
        },
        {
          type: 'scatter',
          symbolSize: 10,
          data: subjectOpenRatesChartData,
          itemStyle: {
            color: chartColours[0].value,
            borderColor: '#FFF',
            borderWidth: 1
          },
          z: 2
        }
      ]
    },
    {
      grid: {
        top: 20,
        left: 20,
        right: 20,
        bottom: 70
      },
      xAxis: {
        type: 'time',
        splitLine: { show: true },
        axisLabel: {
          formatter: value => format(new Date(value), 'haaa').toLowerCase()
        },
        splitNumber: 12,
        min: hourToDate(minTime),
        max: hourToDate(maxTime)
      },
      yAxis: {
        type: 'category',
        data: ['Best for Clicks', 'Best for Opens', 'Most Frequent'],
        axisLabel: { show: false },
        axisLine: { show: false },
        splitLine: { show: false },
        axisTick: { show: false }
      },
      series: [
        {
          type: 'scatter',
          symbolSize: 12,
          itemStyle: { color: chartColours[8].value },
          data: [[hourToDate(sendTimes.bestSendTimeForClicks), 'Best for Clicks']],
          label: {
            show: true,
            formatter: () =>
              'Best for Clicks: ' + format(hourToDate(sendTimes.bestSendTimeForClicks), 'haaa').toLowerCase(),
            position: 'right'
          }
        },
        {
          type: 'scatter',
          symbolSize: 12,
          itemStyle: { color: chartColours[3].value },
          data: [[hourToDate(sendTimes.bestSendTimeForOpens), 'Best for Opens']],
          label: {
            show: true,
            formatter: () =>
              'Best for Opens: ' + format(hourToDate(sendTimes.bestSendTimeForOpens), 'haaa').toLowerCase(),
            position: 'right'
          }
        },
        {
          type: 'scatter',
          symbolSize: 12,
          itemStyle: { color: chartColours[5].value },
          data: [[hourToDate(sendTimes.mostFrequentSendTime), 'Most Frequent']],
          label: {
            show: true,
            formatter: () =>
              'Most Frequent: ' + format(hourToDate(sendTimes.mostFrequentSendTime), 'haaa').toLowerCase(),
            position: 'right'
          }
        }
      ]
    }
  ];

  const settings = {
    dots: true,
    fade: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: false,
    dotsClass: `slick-dots ${slickDotsClass}`,
    beforeChange: (_oldIndex: number, newIndex: number) => {
      setCurrentChartIndex(newIndex);
    }
  };

  return (
    <Card
      elevation={4}
      sx={{
        minHeight: 460,
        maxHeight: 460,
        borderRadius: 3,
        px: 1
      }}
    >
      <CardHeader
        sx={{ pb: 0 }}
        title={(
          <Typography
            variant="h6"
            gutterBottom
            sx={{ fontWeight: 'bold', color: 'primary.main' }}
          >
            {chartHeaders[currentChartIndex]}
          </Typography>
        )}
        action={(
          <Chip label={currentChartIndex < 2 ? 'Last 90 days' : 'Last 20 days'} size="small" />
        )}
      />
      <CardContent sx={{ pt: 0, px: 0 }}>
        <SlickDots bottom={10}>
          <Slider {...settings}>
            {chartOptions.map((option, index) => (
              <Box key={index}>
                <ReactECharts option={option} style={{ height: '390px', width: '100%' }} />
              </Box>
            ))}
          </Slider>
        </SlickDots>
      </CardContent>
    </Card>
  );
};

export default Charts;
